import { DeepMap, ValidateResult } from 'react-hook-form';
import {
  ChangeType,
  GeofencingEnforcementType,
  JobsiteAppCheckInModuleInput,
} from 'apollo/generated/client-operations';
import { Editable } from 'types';
import {
  FormInputTypes,
  getUpdateInputValueFunction,
  GridColSpan,
  TypedFormInputs,
} from 'components/form';
import { SiteAreas } from 'containers/jobsiteConfiguration/components';
import {
  EditJobsiteConfigurationFormData,
  SiteArea,
} from 'containers/jobsiteConfiguration/types';
import {
  ensureNonUndefinedFields,
  getSelectOptionsFromStringEnum,
} from 'utils';
import { Jobsite } from './types';

const FEATURE_LABELS = [
  'Worker App Self Check In',
  'Worker App Admin Check In',
  'Worker App Badge Scanning',
  'Kiosk Access Events',
]
  .map((label) => `"${label}"`)
  .join(', ');

const SITE_AREAS_REQUIRED_MESSAGE = `At least one Site Area is required if any of the following features are enabled: 
${FEATURE_LABELS}`;

export const geofencingEnforcementTypeOptions = getSelectOptionsFromStringEnum(
  GeofencingEnforcementType,
  [GeofencingEnforcementType.None, GeofencingEnforcementType.Hard],
);

export const getGeofencingSectionInputs = ({
  jobsite,
}: {
  jobsite: Jobsite;
}): TypedFormInputs<EditJobsiteConfigurationFormData['geofencing']> => ({
  siteAreas: {
    element: FormInputTypes.CustomInput,
    elementProps: { customInput: SiteAreas },
    validation: {
      validate: jobsite?.isTemplate
        ? undefined
        : (value: Editable<SiteArea>[]): ValidateResult => {
            if (
              !value?.filter((item) => item.changeType !== ChangeType.Removed)
                .length
            ) {
              const {
                workerAppSelfCheckInEnabled,
                workerAppAdminCheckInEnabled,
                workerAppBadgeScanningEnabled,
                workerAppKioskCheckInEnabled,
              } = jobsite?.featuresModule ?? {};
              if (
                workerAppSelfCheckInEnabled ||
                workerAppAdminCheckInEnabled ||
                workerAppBadgeScanningEnabled ||
                workerAppKioskCheckInEnabled
              ) {
                return SITE_AREAS_REQUIRED_MESSAGE;
              }
            }
            // If no check-in features are enabled, we don't need to validate site areas
            return true;
          },
    },
    layout: [GridColSpan.SpanFull],
  },
  geofencingEnforcementType: {
    element: FormInputTypes.Select,
    label: 'Enforcement Type',
    elementProps: {
      options: geofencingEnforcementTypeOptions,
      isClearable: false,
    },
    validation: { required: true },
    layout: [GridColSpan.SpanFull, GridColSpan.SmSpan3],
  },
  crewGeofencingEnforcementType: {
    element: FormInputTypes.Select,
    label: 'Crew Enforcement Type',
    elementProps: {
      options: geofencingEnforcementTypeOptions,
      isClearable: false,
    },
    validation: { required: true },
    layout: [GridColSpan.SpanFull, GridColSpan.SmSpan3],
  },
});

export const getGeofencingSectionDefaultValues = (
  jobsite: Jobsite,
): EditJobsiteConfigurationFormData['geofencing'] => {
  const { areas, geofencingEnforcementType, crewGeofencingEnforcementType } =
    jobsite?.appCheckInModule ?? {};

  return {
    siteAreas:
      areas?.map(({ id, name, checkInRadius, checkInCoordinate }) => ({
        id,
        name,
        radius: checkInRadius,
        latitude: checkInCoordinate?.lat,
        longitude: checkInCoordinate?.lng,
      })) ?? [],
    geofencingEnforcementType:
      geofencingEnforcementTypeOptions.find(
        (option) => option.value === geofencingEnforcementType,
      ) ?? geofencingEnforcementTypeOptions[0],
    crewGeofencingEnforcementType:
      geofencingEnforcementTypeOptions.find(
        (option) => option.value === crewGeofencingEnforcementType,
      ) ?? geofencingEnforcementTypeOptions[0],
  };
};

type GeofencingSectionUpdateInput = Required<
  Pick<
    JobsiteAppCheckInModuleInput,
    'areas' | 'geofencingEnforcementType' | 'crewGeofencingEnforcementType'
  >
>;

export const getGeofencingSectionUpdateInput = (
  geofencing: EditJobsiteConfigurationFormData['geofencing'],
  dirtyFields: DeepMap<EditJobsiteConfigurationFormData['geofencing'], true>,
): GeofencingSectionUpdateInput => {
  const getUpdateInputValue = getUpdateInputValueFunction(
    geofencing,
    dirtyFields,
  );

  const areas: JobsiteAppCheckInModuleInput['areas'] = getUpdateInputValue(
    'siteAreas',
  )
    ?.filter((a) => a.changeType)
    .map((a) => ({
      id: a.id,
      name: a.name,
      checkInRadius: a.radius,
      checkInCoordinate: {
        lat: a.latitude,
        lng: a.longitude,
      },
      isInboundEnabled: true,
      isOutboundEnabled: true,
      changeType: a.changeType,
    }));

  return ensureNonUndefinedFields<GeofencingSectionUpdateInput>({
    areas,
    geofencingEnforcementType: getUpdateInputValue('geofencingEnforcementType'),
    crewGeofencingEnforcementType: getUpdateInputValue(
      'crewGeofencingEnforcementType',
    ),
  });
};
